import { Ratio } from "@/libs/units/percentage";
import type {
  BasicData,
  GasProperties
} from "@/libs/calculations/fluidProperties/d";
import { Rankine } from "@/libs/units/temperature";
import { withoutAbsentGasComponents } from "@/libs/calculations/gasPhase/gasTable";

export type PsuedoCriticalCalculations = {
  psuedoCriticalTemperatureRankine: number;
  psuedoCriticalPressurePSIA: number;
  sourGasA: number;
  sourGasB: number;
  adjustmentFactor: number;
  adjustedPsuedoCriticalTemperatureRankine: number;
  adjustedPsuedoCriticalPressurePSIA: number;
  psuedoReducedTemperature: number;
  psuedoReducedPressure: number;
};
export function calculatePsuedoCriticals(
  basicData: BasicData,
  inSituPressurePSIA: number,
  temperatureF: number,
  gasProperties: GasProperties[]
): PsuedoCriticalCalculations {
  const { gasPercentages } = basicData;
  const gasEntries = withoutAbsentGasComponents(gasProperties);

  const temperatureInRankine = Rankine.toThisUnit(temperatureF);
  // D57: =IF(D23>0,169.2+349.5*D23-74*D23^2,SUM(U78:U91))
  let psuedoCriticalTemperatureRankine;
  if (basicData.gasSpecificGravityOverride > 0) {
    psuedoCriticalTemperatureRankine =
      169.2 +
      349.5 * basicData.gasSpecificGravityOverride -
      74 * basicData.gasSpecificGravityOverride ** 2;
  } else {
    psuedoCriticalTemperatureRankine = gasEntries.reduce(
      (sum, current) => sum + current.yTc,
      0
    );
  }

  // D58: =IF(D23>0,756.8-131.07*D23-3.6*D23^2,SUM(V78:V91))
  let psuedoCriticalPressurePSIA;
  if (basicData.gasSpecificGravityOverride > 0) {
    psuedoCriticalPressurePSIA =
      756.8 -
      131.07 * basicData.gasSpecificGravityOverride -
      3.6 * basicData.gasSpecificGravityOverride ** 2;
  } else {
    psuedoCriticalPressurePSIA = gasEntries.reduce(
      (sum, current) => sum + current.yPc,
      0
    );
  }
  // D61: =SUM(C88:C89)
  const sourGasA =
    Ratio.toThisUnit(gasPercentages.cO2CarbonDioxidePercent) +
    Ratio.toThisUnit(gasPercentages.h2SHydrogenSulfidePercent);
  // D62: =C89
  const sourGasB = Ratio.toThisUnit(gasPercentages.h2SHydrogenSulfidePercent);

  // D64: =120*(D61**0.9-D61**1.6)+15*(D62**0.5-D62**4)
  const adjustmentFactor =
    120 * (sourGasA ** 0.9 - sourGasA ** 1.6) +
    15 * (sourGasB ** 0.5 - sourGasB ** 4);

  // D66: =D57-D64
  const adjustedPsuedoCriticalTemperatureRankine =
    psuedoCriticalTemperatureRankine - adjustmentFactor;

  // D67: =(D58*D66)/(D57-D62*(1-D62)*D64)
  const adjustedPsuedoCriticalPressurePSIA = psuedoCriticalTemperatureRankine
    ? (psuedoCriticalPressurePSIA * adjustedPsuedoCriticalTemperatureRankine) /
      (psuedoCriticalTemperatureRankine -
        sourGasB * (1 - sourGasB) * adjustmentFactor)
    : 0;

  // K18: =K10/D66
  const psuedoReducedTemperature = adjustedPsuedoCriticalTemperatureRankine
    ? temperatureInRankine / adjustedPsuedoCriticalTemperatureRankine
    : 0;

  // K19: =D15/D67
  const psuedoReducedPressure = adjustedPsuedoCriticalPressurePSIA
    ? inSituPressurePSIA / adjustedPsuedoCriticalPressurePSIA
    : 0;

  return {
    psuedoCriticalTemperatureRankine,
    psuedoCriticalPressurePSIA,
    sourGasA,
    sourGasB,
    adjustmentFactor,
    adjustedPsuedoCriticalTemperatureRankine,
    adjustedPsuedoCriticalPressurePSIA,
    psuedoReducedTemperature,
    psuedoReducedPressure
  };
}
